/**
 * Site-wide content module.
 *
 * Every editable string lives in a content module, never inline in a component
 * (spec section 21, CLAUDE.md "Content and configuration"). The owner should be
 * able to change wording without touching layout.
 *
 * Rules that apply to everything added here:
 *   - No provider counts, waitlist counts, ratings, testimonials, or launch
 *     dates. Ever. Not even as placeholder text.
 *   - Legal page copy is supplied by a human and is not written here.
 */

export interface NavLink {
  label: string;
  to: string;
}

export const site = {
  /** Wordmark and prose form. Three words. */
  brandName: 'My Herd Book',
  /** Code, domain, and identifier form. */
  brandSlug: 'myherdbook',
  /** Short qualifier that sits beside the wordmark in the header. */
  tagline: 'Building in Maryland',

  /** First focusable element on every page (spec section 18, keyboard order). */
  skipToContentLabel: 'Skip to main content',
  /** Accessible name for the wordmark link, which is also the home link. */
  homeLinkLabel: 'My Herd Book, home',

  footer: {
    navLabel: 'Footer',
    links: [
      { label: 'Privacy', to: '/privacy' },
      { label: 'Terms', to: '/terms' },
    ] as readonly NavLink[],
    contactLabel: 'Contact',
    addressLabel: 'Mailing address',
    /**
     * Rendered when the deployment has not supplied the value. Deliberately
     * looks broken: an unset public contact address must fail review, not slip
     * out as a plausible-looking blank.
     */
    contactUnset: 'CONTACT_EMAIL not set',
    addressUnset: 'MAILING_ADDRESS not set',
    unsetHint: 'Set this in the deployment environment before launch.',
    /** `{year}` and `{brand}` are substituted at render time. */
    copyrightTemplate: '© {year} {brand}',
    /**
     * The site's single global honest-timeline statement. `brand-voice.md`
     * allows the timeline to be stated once per surface, and this footer is
     * that once — no page repeats a "still being built" framing above it.
     */
    note: "Early access. I'm signing up Maryland owners and providers now, ahead of the network opening.",
  },
} as const;

export type Site = typeof site;
